import { Link } from "react-router-dom";

export type ReadingMode = "dual" | "scroll";

interface Props {
  title: string;
  chapterTitle: string;
  mode: ReadingMode;
  onModeChange: (m: ReadingMode) => void;
  night: boolean;
  onToggleNight: () => void;
  onOpenDir: () => void;
  saved: boolean;
}

export default function ReaderToolbar({
  title,
  chapterTitle,
  mode,
  onModeChange,
  night,
  onToggleNight,
  onOpenDir,
  saved,
}: Props) {
  return (
    <header
      className={`sticky top-0 z-40 flex h-14 items-center justify-between gap-3 border-b px-3 md:px-6 ${
        night
          ? "border-background-800 bg-background-950/95 text-foreground-100"
          : "border-background-200 bg-background-50/95 text-foreground-950"
      }`}
    >
      <div className="flex min-w-0 items-center gap-2.5">
        <Link
          to="/"
          aria-label="返回首页"
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md transition-colors ${
            night ? "hover:bg-background-900" : "hover:bg-background-100"
          }`}
        >
          <i className="ri-arrow-left-line text-lg"></i>
        </Link>
        <div className="hidden min-w-0 flex-col sm:flex">
          <span className="truncate text-sm font-semibold leading-tight">{title}</span>
          <span className={`truncate text-xs ${night ? "text-foreground-400" : "text-foreground-500"}`}>
            {chapterTitle}
          </span>
        </div>
      </div>

      <div
        className={`flex items-center gap-1 rounded-full p-1 ${
          night ? "bg-background-900" : "bg-background-100"
        }`}
      >
        <button
          type="button"
          onClick={() => onModeChange("dual")}
          className={`flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
            mode === "dual"
              ? night
                ? "bg-primary-500 text-background-50"
                : "bg-primary-500 text-background-50"
              : night
              ? "text-foreground-300 hover:text-foreground-50"
              : "text-foreground-600 hover:text-foreground-950"
          }`}
        >
          <i className="ri-book-2-line text-sm"></i>
          <span className="hidden md:inline">双页</span>
        </button>
        <button
          type="button"
          onClick={() => onModeChange("scroll")}
          className={`flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
            mode === "scroll"
              ? "bg-primary-500 text-background-50"
              : night
              ? "text-foreground-300 hover:text-foreground-50"
              : "text-foreground-600 hover:text-foreground-950"
          }`}
        >
          <i className="ri-align-vertically text-sm"></i>
          <span className="hidden md:inline">滚动</span>
        </button>
      </div>

      <div className="flex items-center gap-1.5">
        {saved && (
          <span
            className={`mr-1 hidden items-center gap-1 text-xs sm:inline-flex ${
              night ? "text-foreground-400" : "text-foreground-500"
            }`}
          >
            <i className="ri-check-double-line text-secondary-500"></i>
            进度已保存
          </span>
        )}
        <button
          type="button"
          onClick={onToggleNight}
          aria-label="切换夜间模式"
          className={`flex h-9 w-9 items-center justify-center rounded-md transition-colors ${
            night
              ? "bg-accent-500 text-background-50 hover:bg-accent-600"
              : "hover:bg-background-100"
          }`}
        >
          <i className={`${night ? "ri-sun-line" : "ri-moon-line"} text-lg`}></i>
        </button>
        <button
          type="button"
          onClick={onOpenDir}
          aria-label="章节目录"
          className={`flex h-9 w-9 items-center justify-center rounded-md transition-colors ${
            night ? "hover:bg-background-900" : "hover:bg-background-100"
          }`}
        >
          <i className="ri-list-unordered text-xl"></i>
        </button>
      </div>
    </header>
  );
}